import { useEffect, useState } from 'react';

import { DeckClown } from '@/api/generated/game';
import { playerAt, selectAssignee, useGame } from '@/store/game';
import { RejectionNotice } from '@/ui/RejectionNotice';

import { CardActions } from './CardActions';
import { CardBack } from './CardBack';
import { CardFace } from './CardFace';
import { FlipCard } from './FlipCard';
import { PauseButton } from './PauseButton';
import { SecretTask } from './SecretTask';
import { SecretWarning } from './SecretWarning';
import { deckLook } from './decks';

/**
 * 04 · Карточка на столе, вместе с 06 (секретное задание) и выбором адресата.
 *
 * Три вещи, которые здесь не решаются, а читаются из состояния:
 * кто исполнитель (`pending.assigneeSeat` — после спасателя это уже не активный
 * игрок), кому адресовано (`pending.targetSeats`) и можно ли вообще нажимать
 * (`selectCanAct`). Правил на клиенте нет, все отказы приезжают от сервера.
 *
 * `hidden: true` — секретная карточка чужими глазами: полей карточки в
 * состоянии физически нет, рисовать нечего, и кнопок у зрителя тоже нет.
 */
export function CardScreen() {
  const state = useGame((s) => s.state);
  const assignee = useGame(selectAssignee);
  const acknowledged = useGame((s) => s.secretAcknowledgedFor);

  const pending = state?.pending;
  const card = pending?.card;

  // Переворот привязан к карточке, а не к экрану: без сброса по id следующая
  // карточка приехала бы уже открытой.
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    setFlipped(false);
  }, [card?.id, pending?.hidden]);

  if (!state || !pending) return null;

  const assigneeName = assignee?.name ?? 'игрока';
  const targets = (pending.targetSeats ?? [])
    .map((seat) => playerAt(state, seat)?.name)
    .filter((name): name is string => name != null);

  // Секретная карточка своими глазами: сперва предупреждение, текста ещё нет.
  if (card?.secret && acknowledged !== card.id) {
    return <SecretWarning assigneeName={assigneeName} />;
  }

  // После подтверждения текст открыт, но оформление секретности остаётся:
  // экран должен читаться чужим глазом как «сюда не смотреть» до самого конца.
  // Рубашки и переворота здесь нет — зрелище на секретной карточке лишнее.
  if (card?.secret) {
    return (
      <SecretTask card={card} assigneeName={assigneeName}>
        <RejectionNotice />
        <CardActions key={card.id} />
      </SecretTask>
    );
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)', paddingBottom: 'var(--clown-safe-bottom)' }}
    >
      <div className="flex items-baseline justify-between py-3">
        <p className="text-meta uppercase tracking-widest text-mute">Исполняет {assigneeName}</p>
        <div className="flex items-baseline gap-3">
          {pending.rescued && (
            <p className="text-meta uppercase tracking-widest text-acc">перекинуто</p>
          )}
          <PauseButton />
        </div>
      </div>

      {/* Карта растёт под задание, а экран прокручивается: обрезать текст
          задания нельзя — его читают вслух целиком. */}
      <div className="flex min-h-draw flex-1 flex-col">
        {pending.hidden ? (
          <div className="flex flex-1 flex-col justify-center border-2 border-acc bg-ground p-6">
            <p className="font-display text-task text-acc">Секретное задание</p>
            <p className="mt-3 text-lead text-dim">
              Его читает только {assigneeName}. В вашем состоянии текста нет.
            </p>
          </div>
        ) : (
          <FlipCard
            flipped={flipped}
            onFlip={() => setFlipped(true)}
            back={<CardBack />}
            face={
              card ? (
                <CardFace
                  card={card}
                  {...(targets.length > 0 ? { addressee: targets.join(', ') } : {})}
                />
              ) : null
            }
            // Клоун на кадре ребра заливает экран розовым: этого хватает,
            // чтобы вся комната подняла головы.
            {...(pending.deck === DeckClown ? { flash: deckLook(DeckClown).face } : {})}
          />
        )}
      </div>

      <RejectionNotice />

      {/* Кнопки появляются позже карты: сперва задание читают, потом жмут. */}
      {flipped && !pending.hidden && <CardActions key={card?.id ?? 'card'} />}
    </div>
  );
}
